'use client'

import { toast } from '@/components/common/toast'
import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { useIsBuy1Get1InGiftShopCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { __cCartService } from '@/services/client/__c.cart.service'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { QUERY_KEYS } from '../constants'
import { useCartQuery } from './use-cart'

export interface ExchangeGiftParams {
  giftProductId: string
  giftVariantId: string
  giftProductName?: string
  giftImageUrl?: string
  giftPrice?: number
  mainProductIndexId?: string // index_id của main product từ query params
}

export function useExchangeGift() {
  const queryClient = useQueryClient()
  const { data: cartData } = useCartQuery()
  const t = useTranslations()
  const isBuy1Get1InGiftShopActive = useIsBuy1Get1InGiftShopCampaignActive()

  return useMutation({
    mutationFn: async (params: ExchangeGiftParams) => {
      // Check if buy-1-get-1-in-gift-shop campaign is active
      if (!isBuy1Get1InGiftShopActive) {
        throw new Error(
          t('gift_shop.error.campaign_not_active') ||
            'Campaign không hoạt động, không thể đổi quà'
        )
      }

      // Bắt buộc phải có mainProductIndexId từ query params
      if (!params.mainProductIndexId) {
        throw new Error(
          t('gift_shop.error.no_main_product') ||
            'Không tìm thấy sản phẩm chính. Vui lòng chọn sản phẩm từ giỏ hàng.'
        )
      }

      // Find main product từ cart bằng index_id
      const mainProduct = cartData?.items?.find(
        (item) => item.index_id === params.mainProductIndexId
      )

      if (!mainProduct) {
        throw new Error(
          t('gift_shop.error.no_main_product') ||
            'Không tìm thấy sản phẩm chính trong giỏ hàng'
        )
      }

      // Check if gift price is greater than main product price
      const giftPrice = params.giftPrice || 0
      const mainProductPrice = mainProduct.regular_price || 0

      if (giftPrice > mainProductPrice) {
        throw new Error(
          t('gift_shop.error.gift_price_too_high') ||
            'Giá quà không được lớn hơn giá sản phẩm chính'
        )
      }

      // Tìm quà cũ (nếu đổi quà)
      const existingGift = mainProduct.nestedGift?.[0]
      const existingGiftIndexId = existingGift?.index_id
      const isSelecting = !existingGiftIndexId // Chọn quà nếu không có quà cũ

      // Build payload cho API
      const deviceToken = DeviceTokenHelper.get()
      const payload: {
        product_id: string
        quantity: number
        options: { id: string }
        device_token: string
        index_parent_id: string
        index_id?: string
      } = {
        product_id: params.giftProductId,
        quantity: 1,
        options: {
          id: params.giftVariantId,
        },
        device_token: deviceToken,
        index_parent_id: mainProduct.index_id,
      }

      // Chỉ thêm index_id nếu đang đổi quà (có quà cũ)
      if (existingGiftIndexId) {
        payload.index_id = existingGiftIndexId
      }

      // Call API
      const result = await __cCartService.addGift(payload)

      // Return with product data for toast
      return {
        state: result.state,
        message: result.message,
        giftProductName: params.giftProductName || '',
        giftImageUrl: params.giftImageUrl || '',
        isSelecting,
      }
    },
    onSuccess: (data) => {
      // Show success toast with gift icon
      toast.exchangeGiftSuccess({
        productName: data.giftProductName || '',
        imageUrl: data.giftImageUrl || '',
        isSelecting: data.isSelecting,
      })

      // Invalidate cart queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CART] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CART_MINI] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COUNT_CART_ITEM] })
    },
    onError: (error) => {
      toast.message({
        title: t('title.sorry') || 'Rất tiếc',
        message:
          error instanceof Error
            ? error.message
            : t('gift_shop.error.exchange_failed') ||
              'Lỗi khi đổi quà. Vui lòng thử lại.',
      })
    },
  })
}
